import React, { memo, useRef, useState, useMemo, useEffect } from "react";
import { FixedSizeList as List, ListChildComponentProps } from "react-window";
import {
  Paper,
  Box,
  Typography,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import ArrowRight from "@mui/icons-material/ArrowRight";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { Storage } from "@mui/icons-material";

interface ColumnData {
  columnName: string;
  type: string;
}

type TableEntry = [string, ColumnData[]];

interface FlatItem {
  id: string;
  type: "table" | "group" | "column";
  label: string;
  depth: number;
  columnType?: string;
}

interface Props {
  rows: TableEntry[];
  filterTable: string;
  setFilterTable: React.Dispatch<React.SetStateAction<string>>;
  open?: boolean;
}

const VirtualizedSimpleTreeView = memo(
  ({ rows, filterTable, setFilterTable, open }: Props) => {
    const theme = useTheme();
    const [expandedItems, setExpandedItems] = useState<string[]>([]);

    const containerRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
      if (containerRef.current) {
        setHeight(containerRef.current.offsetHeight);
      }
      const observer = new ResizeObserver((entries) => {
        setHeight(entries[0].contentRect.height);
      });
      if (containerRef.current) observer.observe(containerRef.current);
      return () => observer.disconnect();
    }, [open]);

    const flatData = useMemo(() => {
      const flattened: FlatItem[] = [];
      const expandedSet = new Set(expandedItems);

      rows.forEach(([tableName, columns], index) => {
        const tableId = `table-${index}`;
        const groupId = `${tableId}-columns`;

        flattened.push({
          id: tableId,
          type: "table",
          label: tableName,
          depth: 0,
        });

        if (expandedSet.has(tableId)) {
          flattened.push({
            id: groupId,
            type: "group",
            label: "Colunas",
            depth: 1,
          });

          if (expandedSet.has(groupId)) {
            columns.forEach((col, colIndex) => {
              flattened.push({
                id: `${groupId}-${colIndex}`,
                type: "column",
                label: col.columnName,
                columnType: col.type,
                depth: 2,
              });
            });
          }
        }
      });
      return flattened;
    }, [rows, expandedItems]);

    const Row = ({ index, style }: ListChildComponentProps) => {
      const item = flatData[index];
      if (!item) return null;

      const isTable = item.type === "table";
      const isGroup = item.type === "group";
      const isExpandable = isTable || isGroup;

      return (
        <div style={style}>
          <TreeItem
            itemId={item.id}
            children={isExpandable ? <div /> : undefined}
            label={
              item.type === "column" ? (
                <Tooltip
                  title={`${item.label} ${item.columnType}`}
                  placement="right"
                >
                  <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {item.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {item.columnType}
                    </Typography>
                  </Box>
                </Tooltip>
              ) : (
                item.label
              )
            }
            slots={{
              expandIcon: isTable ? Storage : isGroup ? ArrowRight : undefined,
              collapseIcon: isTable
                ? Storage
                : isGroup
                  ? ArrowDropDown
                  : undefined,
            }}
            sx={{
              ml: item.depth * 1.5,
              borderLeft:
                item.depth > 0
                  ? `1px solid ${theme.palette.primary.main}`
                  : "none",
              "& .MuiTreeItem-content": {
                height: 32,
              },
            }}
          />
        </div>
      );
    };

    return (
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "5px",
          padding: 2,
          gap: 1,
          flex: 1,
          height: "100%",
          maxWidth: 300,
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Tabelas
          </Typography>
          <TextField
            label="Filtrar"
            variant="filled"
            size="small"
            fullWidth
            value={filterTable}
            onChange={(e) => setFilterTable(e.target.value)}
          />
        </Box>

        <Box ref={containerRef} sx={{ flex: 1, mt: 1, minHeight: 0 }}>
          {height > 0 && (
            <SimpleTreeView
              expandedItems={expandedItems}
              onItemExpansionToggle={(_, itemId, isExpanded) => {
                setExpandedItems((prev) =>
                  isExpanded
                    ? [...prev, itemId]
                    : prev.filter((id) => id !== itemId),
                );
              }}
            >
              <List
                height={height}
                itemCount={flatData.length}
                itemSize={32}
                width="100%"
              >
                {Row}
              </List>
            </SimpleTreeView>
          )}
        </Box>
      </Paper>
    );
  },
);

export default VirtualizedSimpleTreeView;
